import React from 'react';
import { IconBox, PaperHeaderBox, StatusTag } from './style';
import { alpha, Box, Typography, useTheme } from '@mui/material';

const statusContants = {
    pending: 'pending',
    completed: 'completed',
};

/**
 * PaperHeader
 *
 * A component that displays a header section with an icon, heading text, status tag, and optional right-side button.
 * Useful for headers in paper or card components where a title, icon, and status are displayed alongside an action button.
 *
 * @param {JSX.Element} icon - Icon component to display on the left side. Defaults to CheckCircleOutline.
 * @param {string} headingText - Text for the main heading.
 * @param {string} status - The current status, either 'pending' or 'completed'. Displays corresponding color and label.
 * @param {JSX.Element} rightSideButton - Custom button element displayed on the right side.
 * @param {object} headingStyles - Custom styles for the heading text.
 * @param {...object} props - Additional props to pass to the PaperHeaderBox container.
 *
 * @returns {JSX.Element} - A styled header component with an icon, heading, status, and action button.
 */

function PaperHeader({ icon = null, iconSx = {}, headingText = '', status = '', rightSideButton = null, headingStyles = {}, normalHeading = false, ...props }) {
    const theme = useTheme();
    return (
        <PaperHeaderBox {...props}>
            <Box
                className="headingBox"
                display={'flex'}
                justifyContent={'flex-start'}
                alignItems={'center'}
                sx={{
                    width: {
                        xs: '100%',
                        sm: '100%',
                        md: 'unset',
                    },
                }}
            >
                {icon && (
                    <IconBox className="icon-box" sx={{ ...iconSx }}>
                        {icon}
                    </IconBox>
                )}
                <Typography
                    component={'h1'}
                    variant={normalHeading ? 'subHeading' : 'heading1'}
                    color={'text.primary'}
                    className="paper-heading"
                    fontFamily={normalHeading ? 'Barlow' : '"Barlow Condensed"'}
                    sx={{ ...headingStyles }}
                >
                    {headingText || 'Heading'}
                </Typography>
                {status && (
                    <StatusTag
                        component={'span'}
                        sx={{
                            color: theme.palette.success.main,
                            backgroundColor: alpha(theme.palette.success.main, 0.08),

                            ...(status === statusContants.pending && {
                                color: theme.palette.error.main,
                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                            }),
                        }}
                    >
                        {status === statusContants.completed ? 'completed' : 'in progress'}
                    </StatusTag>
                )}
            </Box>

            {rightSideButton}
        </PaperHeaderBox>
    );
}

export default PaperHeader;
